// catppuccin — Latte (light) / Mocha (dark), accent = mauve.
// Mapped from the upstream scheme and AA-validated (≥4.5:1 on every
// contract-mandated text pair, both modes) before landing — see
// scratchpad/catppuccin-notes.md for the per-pair tables.

import type { Hex, ThemeDefinition } from "./types.js";

// One source for the light primary ink: palette.light.ink and the "none"
// intensity band text read it, so a retune of the ink cannot leave a stale copy.
const lightInk: Hex = "#43455c";

export const catppuccin: ThemeDefinition = {
  palette: {
    // Accent = Catppuccin's SIGNATURE mauve (not peach — peach ≈ JL brand orange,
    // which defeats the point in light mode; Antenore 2026-07-01). White on Latte
    // mauve #8839ef = 5.42:1 AA-pass; Mocha mauve #cba6f7 takes near-black text.
    light: {
      brand500: "#8839ef", brand600: "#7526d3", brand100: "#e7dbfb",
      // DS-11/DS-21 (see design-tokens.ts): Latte mauve already reads as text
      // and already takes white, so this family doesn't move a pixel.
      brandText: "#7526d3", brandPressed: "#7526d3", inkOnBrandPressed: "#ffffff",
      bg: "#eff1f5", surface: "#e6e9ef", surface2: "#dce0e8",
      border: "#acb0be", borderStrong: "#767a8e", scrim: "rgba(0,0,0,0.5)",
      // DS-8 (2026-07-14): ink2 was Latte subtext0 #6c6f85 = 3.73:1 on surface2,
      // under the floor on the DEFAULT theme, while ink3 held Latte subtext1 —
      // which is the STRONGER of the two. The ladder was inverted: tertiary text
      // outranked secondary. ink2 now sits between text and subtext1, so
      // ink > ink2 > ink3 reads in that order and all three clear AA.
      //
      // DS-22 (Antenore, 2026-07-26) — comfort pass, route C2. The three inks
      // darken; the SURFACES do not move. Primary sat at 6.04:1, i.e. under
      // AAA on the family the phones actually run, because here `surface2` is
      // the DARKEST surface and squeezes text from above. 6.04 -> 7.07.
      //
      // Route C1 (lighten the surfaces instead) was considered and rejected:
      // it would flatten surface<->surface2 to ~1.02, eating the very border
      // separation VB-PAL-4 exists to save. Darkening the ink costs nothing
      // structurally, and the ladder gets MORE legible as a ladder, not less
      // (steps on surface2 go 0.77/0.54 -> 1.46/0.80, against a gate of 0.30).
      // He is myopic and reads this theme by default: "legible but barely" is
      // not the bar ([[feedback_user_visual_accessibility]]).
      ink: lightInk, ink2: "#50546d", ink3: "#5b5e75", inkOnBrand: "#ffffff",
      inkOnDanger: "#ffffff", inkOnWarn: "#ffffff",
      pr: "#865511", prFill: "#ece1d2",
      complete: "#286419", completeFill: "#d3e4d5", // DS-13: was #2d701e (4.60 on surface2, borderline) → 5.42, comfortable floor for reduced vision
      danger: "#b20d30", dangerFill: "#eacdd7",
      warn: "#865511", warnFill: "#ece1d2",
    },
    dark: {
      brand500: "#cba6f7", brand600: "#b799ed", brand100: "#2e2540",
      // DS-11/DS-21: Mocha mauve, unchanged — text 4.83 worst, pressed 7.85.
      brandText: "#b799ed", brandPressed: "#b799ed", inkOnBrandPressed: "#11111b",
      // DS-8 comfort (2026-07-14, Antenore approved): surface2 was #45475a —
      // the LIGHTEST tone in the dark stack, so it squeezed body text from
      // above and capped even the PRIMARY ink at 6.31:1, under AAA. The inks
      // were never the problem. Darkening it toward `surface` keeps the stack
      // (bg < surface < surface2) and lifts all three tiers at once:
      // 6.31/4.10/5.15 -> 7.98/7.07/6.52. He reads dark, and "legible but
      // barely" is not the bar ([[feedback_user_visual_accessibility]]).
      // NOTE: #363849 is NOT one of the official 26 Mocha swatches (the old
      // #45475a was Mocha `surface1`). Hue/saturation are held — it interpolates
      // on the family ramp between surface0 and surface1 — but this family is no
      // longer byte-for-byte upstream Catppuccin here. Same trade DS-9/DS-13
      // already made elsewhere: the floor wins over swatch fidelity.
      bg: "#1e1e2e", surface: "#313244", surface2: "#363849",
      border: "#585b70", borderStrong: "#81869d", scrim: "rgba(0,0,0,0.5)",
      // DS-8: same inversion in Mocha — ink2 was subtext0 (4.10 on surface2),
      // ink3 subtext1. ink2 now sits between text and subtext1 (5.59 worst).
      ink: "#cdd6f4", ink2: "#c2cae4", ink3: "#bac2de", inkOnBrand: "#11111b",
      inkOnDanger: "#11111b", inkOnWarn: "#11111b",
      pr: "#f9e2af", prFill: "#413d43",
      complete: "#a6e3a1", completeFill: "#343e40",
      danger: "#f38ba8", dangerFill: "#402f42",
      warn: "#f9e2af", warnFill: "#413d43",
    },
  },
  intensityScale: {
    light: {
      work: { fill: "#a9d1a4", on: "#11111b", soft: "#d3e4d5", text: "#2d701e" },
      moderate: { fill: "#f8cb8b", on: "#11111b", soft: "#ece1d2", text: "#865511" },
      heavy: { fill: "#ebacb4", on: "#11111b", soft: "#eed5db", text: "#ac343e" },
      max: { fill: "#c6a7f3", on: "#11111b", soft: "#dfd4f4", text: "#7a33d7" },
      none: { fill: "#bcc0cc", on: "#11111b", soft: "#ccd0da", text: lightInk },
    },
    dark: {
      work: { fill: "#4e6356", on: "#ffffff", soft: "#343e40", text: "#a6e3a1" },
      moderate: { fill: "#7f6e5c", on: "#ffffff", soft: "#413d43", text: "#f9e2af" },
      heavy: { fill: "#664c5a", on: "#ffffff", soft: "#3f3342", text: "#eba0ac" },
      max: { fill: "#5b4e74", on: "#ffffff", soft: "#3a344e", text: "#cba6f7" },
      // fill/soft track surface2/surface (the contract rosepine already keeps
      // in both modes). They held the OLD surface2 #45475a by value, so the
      // DS-8 comfort pass left the "no band" cell 1.27:1 brighter than every
      // other neutral chip on the same screen.
      none: { fill: "#363849", on: "#ffffff", soft: "#313244", text: "#cdd6f4" },
    },
  },
  // Semantic rested→over-used ramp on Catppuccin accents (green→gold→peach→red),
  // stops spread for perceptible low/mid separation.
  heatmapRamp: {
    light: ["#40a02b", "#8ab82e", "#ffbb33", "#fe640b", "#d20f39"],
    dark: ["#a6e3a1", "#c4d96a", "#ffefc2", "#fab387", "#eb6f7d"],
  },
  // Monochrome effort ramp on the signature mauve (see design-tokens.ts
  // effortRamp for the semantics; pairs AA-validated by effortRampContrast).
  effortRamp: {
    light: [
      // riscaldamento (RIR 10-6)
      { fill: "#cedefd", on: "#0b1526" },
      { fill: "#acc7fb", on: "#0b1526" },
      { fill: "#8aaffa", on: "#0b1526" },
      { fill: "#6898f8", on: "#0b1526" },
      { fill: "#4581f7", on: "#0b1526" },
      // lavoro leggero (5-4)
      { fill: "#e9aa54", on: "#0b1526" },
      { fill: "#d0841b", on: "#0b1526" },
      // serie allenante (3-0)
      { fill: "#c52f63", on: "#ffffff" },
      { fill: "#aa2955", on: "#ffffff" },
      { fill: "#8f2247", on: "#ffffff" },
      { fill: "#731c3a", on: "#ffffff" },
    ],
    dark: [
      // riscaldamento (RIR 10-6)
      { fill: "#042862", on: "#ffffff" },
      { fill: "#053582", on: "#ffffff" },
      { fill: "#0742a1", on: "#ffffff" },
      { fill: "#084fc1", on: "#ffffff" },
      { fill: "#0a5ce1", on: "#ffffff" },
      // lavoro leggero (5-4)
      { fill: "#be870e", on: "#07090f" },
      { fill: "#eeac1b", on: "#07090f" },
      // serie allenante (3-0)
      { fill: "#ae0937", on: "#ffffff" },
      { fill: "#df0c46", on: "#ffffff" },
      { fill: "#f32a62", on: "#07090f" },
      { fill: "#f65b85", on: "#07090f" },
    ],
  },
};
